<template>
  <div class="w-full px-6 py-5 gap-4">
    <template v-if="review.entity.value?.studies?.length > 0">
      <div
        class="inline-grid w-full border-t border-r border-neutral-border-default bg-neutral-background-default rounded-t-lg"
        :style="gridStyle"
      >
        <div class="bg-neutral-background-default h-12.5 rounded-tl-lg sticky top-13 left-0 z-51">
          <div
            class="h-full text-sm px-5 py-3.5 justify-between border-neutral-border-default border-b border-l flex items-center font-medium rounded-tl-lg"
          >
            <span class="text-neutral-text-title font-medium">Records</span>
            <div class="flex items-center gap-2">
              <Sort />
              <ReviewItemFilters />
            </div>
          </div>
        </div>

        <div class="bg-neutral-background-default h-12.5 sticky top-13 left-0 z-40">
          <div
            class="h-full text-sm text-neutral-text-title flex p-3.5 items-center font-medium justify-between border-l border-r border-b border-neutral-border-default"
          >
            Duplication
          </div>
        </div>

        <div class="bg-neutral-background-default h-12.5 sticky top-13 left-0 z-40">
          <div
            class="h-full text-sm border-b border-r border-neutral-border-default text-neutral-text-title flex p-3.5 items-center font-medium justify-between"
          >
            Screening
          </div>
        </div>

        <div class="rounded-tr-lg bg-neutral-background-default h-12.5 sticky top-13 left-0 z-40">
          <div
            class="h-full text-sm border-b border-neutral-border-default text-neutral-text-title flex p-3.5 items-center font-medium justify-between"
          >
            Citation
          </div>
        </div>

        <template v-for="reviewItem in paginatedReviewItems" :key="reviewItem.id">
          <ReviewItemScreening
            :review-item="reviewItem"
            :is-metadata-expanded="isMetadataExpanded"
          />
        </template>
      </div>
      <div
        v-if="review.filteredReviewItems.value.length > 0"
        class="w-full flex items-center justify-between px-5 py-4 sticky bottom-0 bg-neutral-background-default border-r border-l border-b border-neutral-border-default rounded-b-lg z-50"
      >
        <p class="text-base font-medium text-neutral-text-title">
          Total Records: {{ review.filteredReviewItems.value.length }}
        </p>
        <Pagination
          v-model:items-per-page="itemsPerPage"
          v-model:page="currentPage"
          :total-items="review.filteredReviewItems.value.length"
        />
      </div>
    </template>
    <div v-else-if="review.filtering.hasFiltersApplied.value">No articles match your filters</div>
    <div v-else>This review is empty</div>

    <button
      class="fixed right-0 top-1/2 w-12.5 h-12.5 p-2.5 bg-[#F97316] rounded-l-lg shadow-md flex justify-center items-center shrink-0 z-[56]"
      @click="toggleChart"
    >
      <div v-if="review.filtering.hasFiltersApplied.value" class="absolute -top-1 -left-1">
        <span class="relative flex h-2 w-2">
          <span
            class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"
          ></span>
          <span class="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
        </span>
      </div>
      <ChartPieIcon class="w-5 h-5 text-white fill-white" />
    </button>

    <Transition name="fade">
      <div
        v-if="shouldShowChart"
        class="fixed inset-0 bg-black/50 z-[53]"
        @click="shouldShowChart = false"
      />
    </Transition>
    <ScreeningChart v-model="shouldShowChart" />
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { injectStrict } from "@app/utils/injectStrict";
import Sort from "@app/views/Review/Execute/Sort/Sort.vue";
import { ChartPieIcon } from "@heroicons/vue/24/outline";
import { useStorage } from "@vueuse/core";
import { useRoute, useRouter } from "vue-router";
import { ReviewKey } from "../../use-review";
import ReviewItemFilters from "../SearchAndFilters/ReviewItemFilters.vue";
import ScreeningChart from "./ScreeningChart.vue";
import ReviewItemScreening from "./ReviewItemScreening/ReviewItemScreening.vue";
import Pagination from "@app/components/Global/Pagination/Pagination.vue";

const review = injectStrict(ReviewKey);

const route = useRoute();
const router = useRouter();

const currentPage = computed({
  get() {
    return Number(route.query.page) || 1;
  },
  set(value: number) {
    router.replace({
      query: {
        ...route.query,
        page: value.toString(),
      },
    });
  },
});
watch(
  currentPage,
  (newPage) => {
    router.replace({
      query: {
        ...route.query,
        page: newPage.toString(),
      },
    });
  },
  {
    immediate: true,
  },
);

const itemsPerPage = computed({
  get() {
    return Number(route.query.itemsPerPage) || 5;
  },
  set(value: number) {
    router.replace({
      query: {
        ...route.query,
        itemsPerPage: value.toString(),
      },
    });
  },
});

const paginatedReviewItems = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return review.filteredReviewItems.value.slice(start, start + itemsPerPage.value);
});

const isMetadataExpanded = useStorage("isMetadataExpanded-" + review.entity.value.id, true);

const gridStyle = computed(() => {
  // 12-column grid: first column 6 cols, remaining 3 columns 2 cols each
  return {
    display: "grid",
    gridTemplateColumns: "6fr 2fr 2fr 2fr",
  };
});

const isChartShown = ref(false);

const shouldShowChart = computed({
  get() {
    return isChartShown.value && review.entity.value?.studies?.length > 0;
  },
  set(value: boolean) {
    isChartShown.value = value;
  },
});

function toggleChart() {
  isChartShown.value = !isChartShown.value;
}

watch(
  () => review.filtering.terms.value,
  () => {
    currentPage.value = 1;
  },
);

watch(
  () => review.filtering.filters.value,
  () => {
    currentPage.value = 1;
  },
  { deep: true },
);

watch(itemsPerPage, () => {
  currentPage.value = 1;
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
